import React, { useState } from "react";
import { RiAddBoxFill, RiEdit2Fill } from "react-icons/ri";
import { MdCheckCircle, MdAssignmentReturn } from "react-icons/md";
import ActionReferralModal from "../modals/ActionRefferalModal";
import { usePatient } from "../../contexts";
import { useNavigate } from "react-router-dom";
import ProcedureEditsModal from "../modals/ProcedureEditsModal";
import Spinner from "../UI/Spinner";
import TableSkeleton from "./TableSkeleton";
import { put } from "../../utility/fetch";
import notification from '../../utility/notification';
import LabReportTable from "../modals/LabReports";
import { calculateAge } from "../../utility/general";
import ProcedureEditsReportModal from "../modals/ProcedureEditsReportModal";
import Tooltip from '@mui/material/Tooltip';

function WalkInTable({ data, fetch, getProcedures, sendBack, manage = false, currentPage, itemsPerPage, readOnly }) {
  const { setPatientInfo } = usePatient()
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [referralId, setRefferalId] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralInfo, setReferralInfo] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' or 'review'
  const [confirmingRow, setConfirmingRow] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const navigate = useNavigate()
  const [showProcedureEditModal, setShowProcedureEditModal] = useState(false);
  const [referredPatients, setReferredPatients] = useState([
    {
      patientId: 'H3787',
      firstName: "John",
      lastName: "Doe",
      hospitalName: "General Hospital",
      doctor: "Dr. Smith",
      dateCreated: "2023-01-01",
      labWorkRequested: ["Blood Test", "X-Ray"],
      severity: "High",
      diagnosis: "Hypertension",
      timeInitiated: "10:00 AM",
      hmoCover: 2000000
    },
    {
      patientId: 'H3097',
      firstName: "Jane",
      lastName: "Doe",
      hospitalName: "City Clinic",
      doctor: "Dr. Brown",
      dateCreated: "2023-02-01",
      labWorkRequested: ["MRI", "CT Scan"],
      severity: "Medium",
      diagnosis: "Migraine",
      timeInitiated: "11:00 AM",
      hmoCover: 150000
    },
    {
      patientId: 'H3798',
      firstName: "Alice",
      lastName: "Johnson",
      hospitalName: "Health Center",
      doctor: "Dr. White",
      dateCreated: "2023-03-01",
      labWorkRequested: ["Ultrasound", "Blood Test"],
      severity: "Low",
      diagnosis: "Anemia",
      timeInitiated: "12:00 PM",
      hmoCover: 40000
    }
  ]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isWalkin, setIsWalkin] = useState(false);
  const [color, setColor] = useState('');
  const [template, setTemplate] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);

  const { setPatientId } = usePatient();

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPatientInfo(null)
  };

  const continueUpdate = (id, data) => {
    setPatientId(id);
    setPatientInfo(data)
    setReferralInfo(data);
    setRefferalId(data?.referralId);
    setShowProcedureEditModal(true)
  };
  // Open confirmation dialog
  const openConfirmDialog = (action, row) => {
    setConfirmAction(action);
    setConfirmingRow(row);
    setConfirmDialogOpen(true);
  };

  // Close confirmation dialog
  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
    setConfirmingRow(null);
    setReviewComment(''); // Reset comment on close
  };

  // Execute the action after confirmation
  const handleConfirmAction = async () => {
    closeConfirmDialog();
    await performApproveOrReview(confirmAction, confirmingRow);
  };

  // Perform approve or send back for review
  const performApproveOrReview = async (action, row) => {
    if (!row || !row.id) {
      notification({ message: 'Invalid row data', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const patientLabReportId = row.id;
      const isApproved = action === 'approve' ? true : false;

      // Handle both patientTestReports (review response) and walkInTestRequests
      const reports = row.patientTestReports || row.walkInTestRequests || [];
      const payload = {
        testReports: reports.map(report => ({
          testReportId: report.id || 0,
          isApproved: isApproved,
          ...(action !== 'approve' && { reviewComment: reviewComment }), // Add comment if it's a review action
        }))
      };

      const res = await put(
        `/walkinpatientlabreport/lab-report/${patientLabReportId}/approve-walk-in-patient-lab-report`,
        payload
      );

      if (res?.statusCode === 200 || res?.status === 200) {
        const message = action === 'approve'
          ? 'Lab report approved successfully'
          : 'Lab report sent back for review';
        notification({ message, type: 'success' });
        getProcedures();
      } else {
        notification({ message: 'Failed to update report', type: 'error' });
      }
    } catch (err) {
      console.error(err);
      notification({ message: 'Failed to update report', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  console.log(referralInfo);

  // helper: compute completion status banner
  const renderCompletionBanner = (row) => {
    const requests = row?.patientTestReports ?? row?.walkInTestRequests ?? [];
    const allDone = Array.isArray(requests) && requests.length > 0 && requests.every(r => r?.isReportDone === true);
    const statusText = allDone ? 'COMPLETE' : 'INCOMPLETE';
    const styles = allDone
      ? { background: '#e6f9ee', color: '#15683a', border: '1px solid #c7efd0' }
      : { background: '#fff4e6', color: '#a04f00', border: '1px solid #ffd8a8' }

    return (
      <td>
        <div style={{ display: 'inline-block', padding: '6px 10px', borderRadius: 8, fontWeight: 800, fontSize: 12, textTransform: 'uppercase', ...styles }}>
          {statusText}
        </div>
      </td>
    );
  };

  // Helper to extract first/last name from patientFullName or use firstName/lastName
  const getFirstName = (row) => {
    if (row.firstName) return row.firstName;
    if (row.patientFullName) {
      const parts = row.patientFullName.split(' ');
      return parts[0];
    }
    if (row?.walkInPatient) {
      return row.walkInPatient.firstName;
    }
    return 'N/A';
  };

  const getLastName = (row) => {
    if (row.lastName) return row.lastName;
    if (row.patientFullName) {
      const parts = row.patientFullName.split(' ');
      return parts.slice(1).join(' ') || 'N/A';
    }
    if (row?.walkInPatient) {
      return row.walkInPatient.lastName;
    }
    return 'N/A';
  };

  // Helper to get lab services from either patientTestReports or walkInTestRequests
  const getLabServices = (row) => {
    const reports = row?.patientTestReports || row?.walkInTestRequests || [];
    return reports.map(r => r?.internalLabService?.name).filter(Boolean);
  };

  // Helper to get approval info from first patientTestReport
  const getApprovalInfo = (row) => {
    const reports = row?.patientTestReports || row?.walkInTestRequests || [];
    if (reports.length > 0) {
      return {
        userToApprove: reports[0]?.userToApprove,
        isApproved: reports[0]?.isApproved,
        approvedDate: reports[0]?.approvedDate,
        revertBack: reports[0]?.revertBack
      };
    }
    return {};
  };

  // Sort data by createdOn/createdAt in descending order
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.createdOn || a.createdAt);
    const dateB = new Date(b.createdOn || b.createdAt);
    return dateB - dateA;
  });

  const openLabReportTable = (row) => {
    setReferralInfo(row);
    setIsWalkin(true); // Set based on context
    setColor('#3C7E2D'); // Set color as needed
    setTemplate(row?.patientTestReports || row?.walkInTestRequests || []); // Pass reports
    setCurrentTemplate(row?.patientTestReports?.[0] || row?.walkInTestRequests?.[0] || null);
    setShowReportModal(true);
  };

  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    return (
      <>
        <div className="w-100">
          <div className="tabs m-t-10 bold-text"></div>
          <div className="flex flex-col gap-12 m-t-20">
            {loading ? (
              <TableSkeleton />
            ) : (
              Array.isArray(sortedData) && sortedData?.map((row, index) => {
                const approvalInfo = getApprovalInfo(row);
                const requests = row?.patientTestReports ?? row?.walkInTestRequests ?? [];
                const allDone = Array.isArray(requests) && requests.length > 0 && requests.every(r => r?.isReportDone === true);
                const statusText = allDone ? 'COMPLETE' : 'INCOMPLETE';
                const statusColor = allDone ? '#15683a' : '#a04f00';
                const statusBg = allDone ? '#e6f9ee' : '#fff4e6';

                return (
                  <div key={row?.id || index} className="card shadow-sm m-b-20" style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                    <div className="flex flex-h-between flex-v-center" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                      <h4 className="m-0" style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>{row?.patientFullName || `${getFirstName(row)} ${getLastName(row)}`}</h4>
                      <span style={{ fontSize: '12px', color: '#666' }}>{row?.walkInPatient?.walkInPatientRef || row?.patientRef}</span>
                    </div>
                    <div style={{ padding: '15px' }}>
                      <div className="flex flex-h-between m-b-10">
                         <span className="badge">Age: {row?.age || row?.walkInPatient?.age || calculateAge(row?.walkInPatient?.dateOfBirth) || 'N/A'}</span>
                         <span style={{ fontSize: '12px', color: '#888' }}>{new Date(row?.createdOn || row?.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="m-b-15">
                        <span className="text-uppercase text-muted block m-b-5" style={{ fontSize: '11px', fontWeight: 'bold', color: '#888' }}>LAB TESTS</span>
                        {getLabServices(row).map((service, i) => (
                          <div className="m-b-5" key={i} style={{ fontSize: '14px' }}>{i + 1}. {service}</div>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-10 m-b-10" style={{gap: '10px'}}>
                         <div className="flex flex-col">
                            <span className="text-uppercase text-muted" style={{ fontSize: '10px', fontWeight: 'bold' }}>REVERTED</span>
                            <span style={{ color: approvalInfo?.revertBack ? '#c5192d' : '#155724', fontWeight: '600', fontSize: '13px' }}>{approvalInfo?.revertBack ? 'Yes' : 'No'}</span>
                         </div>
                         <div className="flex flex-col">
                            <span className="text-uppercase text-muted" style={{ fontSize: '10px', fontWeight: 'bold' }}>APPROVAL</span>
                            <span style={{ color: approvalInfo?.isApproved ? '#155724' : '#856404', fontWeight: '600', fontSize: '13px' }}>{approvalInfo?.isApproved ? 'Approved' : 'Pending'}</span>
                         </div>
                         <div className="flex flex-col">
                            <span className="text-uppercase text-muted" style={{ fontSize: '10px', fontWeight: 'bold' }}>STATUS</span>
                            <span style={{ color: statusColor, backgroundColor: statusBg, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>{statusText}</span>
                         </div>
                      </div>
                    </div>
                    <div className="flex flex-h-end" style={{ padding: '10px 15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                        {manage ? (
                          <div style={{ display: 'flex', gap: 15, justifyContent: 'center', alignItems: 'center' }}>
                              <button onClick={() => openLabReportTable(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0066cc', fontSize: '20px' }} title="View Report">📋</button>
                              <button onClick={() => openConfirmDialog('approve', row)} disabled={loading} style={{ background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, color: '#28a745' }} title="Approve"><MdCheckCircle size={24} /></button>
                              <button onClick={() => openConfirmDialog('review', row)} disabled={loading} style={{ background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, color: '#ffa500' }} title="Send Back"><MdAssignmentReturn size={24} /></button>
                          </div>
                        ) : (
                          <RiEdit2Fill onClick={() => continueUpdate(row?.id, row)} className="pointer" style={{ width: '24px', height: '24px', color: '#3C7E2D' }} />
                        )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        {confirmDialogOpen && (
        <div className="modal">
          <div className="modal-contents flex flex-direction-v flex-v-center" style={{ maxWidth: '400px' }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ marginTop: 0 }}>Confirm Action</h2>
              <p style={{ color: '#666', marginBottom: 24 }}>Are you sure you want to <strong>{confirmAction === 'approve' ? 'approve' : 'send back for review'}</strong> this lab report?</p>
              {confirmAction === 'review' && (
                <div style={{ marginBottom: '24px' }}>
                  <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Add a comment for the review (optional)..." style={{ width: '100%', minHeight: '80px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button onClick={closeConfirmDialog} className="submit-btn" style={{ backgroundColor: '#999', flex: 1 }}>Cancel</button>
                <button onClick={handleConfirmAction} disabled={loading} className="submit-btn" style={{ backgroundColor: confirmAction === 'approve' ? '#28a745' : '#ffa500', flex: 1 }}>{loading ? 'Processing...' : (confirmAction === 'approve' ? 'Approve' : 'Send Back')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showReportModal && <LabReportTable closeModal={() => setShowReportModal(false)} patientInfo={referralInfo} report={referralInfo} vitalInfo={referralInfo?.vital} walkin={isWalkin} open={showReportModal} color={color} template={template} currentTemplate={1} review={true} onClose={() => setShowReportModal(false)} />}
      {showProcedureEditModal && (!sendBack ? <ProcedureEditsModal open={showProcedureEditModal} closeModal={() => setShowProcedureEditModal(false)} patientInfo={referralInfo} referredPatients={referredPatients} referralId={referralId} readOnly={readOnly} getProcedures={getProcedures} noDoctor={true} walkin={true} review={manage} /> : <ProcedureEditsReportModal open={showProcedureEditModal} closeModal={() => setShowProcedureEditModal(false)} patientInfo={referralInfo} referredPatients={referredPatients} referralId={referralId} readOnly={readOnly} getProcedures={getProcedures} noDoctor={true} walkin={true} review={manage} />)}
      </>
    );
  }

  return (
    <>
      <div className="w-100">
        <div className=" tabs m-t-10 bold-text"></div>

        <div className="w-100 none-flex-item m-t-40">
          <table className="bordered-table">
            <thead className="border-top-none">
              <tr className="border-top-none">
                <th className="center-text">Lab Id</th>
                <th className="center-text">Patient Name</th>
                <th className="center-text">Age</th>
                <th className="center-text">Lab Tests</th>
                {/* <th className="center-text">Approved By</th> */}
                <th className="center-text">Reverted</th>
                <th className="center-text">Approval Status</th>
                <th className="center-text">Date Created</th>
                <th className="center-text">Completion Status</th>
                <th className="center-text">Action</th>
              </tr>
            </thead>
            {loading ?
              <TableSkeleton /> : <tbody className="white-bg view-det-pane">
                {Array.isArray(sortedData) && sortedData?.map((row, index) => {
                  const approvalInfo = getApprovalInfo(row);
                  return (
                    <tr className="pointer hovers" key={row?.id || index}>
                      <td>{row?.walkInPatient?.walkInPatientRef || row?.patientRef}</td>
                      <td>{row?.patientFullName || `${getFirstName(row)} ${getLastName(row)}`}</td>
                      <td className="center-text">{row?.age || row?.walkInPatient?.age || calculateAge(row?.walkInPatient?.dateOfBirth) || 'N/A'}</td>
                      <td>
                        {getLabServices(row).map((service, i) => (
                          <div className="m-b-5" key={i}>{i + 1}. {service}</div>
                        ))}
                      </td>
                      {/* <td>{approvalInfo?.userToApprove ? `${approvalInfo.userToApprove.firstName} ${approvalInfo.userToApprove.lastName}` : 'N/A'}</td> */}
                      <td className="center-text">
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: approvalInfo?.revertBack ? '#ffe6e6' : '#e6f9ee',
                          color: approvalInfo?.revertBack ? '#c5192d' : '#155724'
                        }}>
                          {approvalInfo?.revertBack ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="center-text">
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: approvalInfo?.isApproved ? '#d4edda' : '#fff3cd',
                          color: approvalInfo?.isApproved ? '#155724' : '#856404'
                        }}>
                          {approvalInfo?.isApproved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td>{new Date(row?.createdOn || row?.createdAt).toLocaleDateString()}</td>
                      {renderCompletionBanner(row)}
                      <td>
                        {manage ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                            <Tooltip title="View Lab Report">
                              <button
                                onClick={() => openLabReportTable(row)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0066cc', fontSize: '20px' }}
                              >
                                📋
                              </button>
                            </Tooltip>
                            <Tooltip title="Approve Report">
                              <button
                                onClick={() => openConfirmDialog('approve', row)}
                                disabled={loading}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  opacity: loading ? 0.5 : 1,
                                  color: '#28a745'
                                }}
                              >
                                <MdCheckCircle size={24} />
                              </button>
                            </Tooltip>
                            <Tooltip title="Send Back for Review">
                              <button
                                onClick={() => openConfirmDialog('review', row)}
                                disabled={loading}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  opacity: loading ? 0.5 : 1,
                                  color: '#ffa500'
                                }}
                              >
                                <MdAssignmentReturn size={24} />
                              </button>
                            </Tooltip>
                          </div>
                        ) : (
                          <span style={{ color: '#3C7E2D' }} className="flex flex-h-center">
                            <RiEdit2Fill
                              onClick={() => continueUpdate(row?.id, row)}
                              className="pointer"
                              style={{ width: '24px', height: '24px' }}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>}
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmDialogOpen && (
        <div className="modal">
          <div className="modal-contents flex flex-direction-v flex-v-center" style={{ maxWidth: '400px' }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ marginTop: 0 }}>Confirm Action</h2>
              <p style={{ color: '#666', marginBottom: 24 }}>
                Are you sure you want to <strong>{confirmAction === 'approve' ? 'approve' : 'send back for review'}</strong> this lab report?
              </p>
              {confirmAction === 'review' && (
                <div style={{ marginBottom: '24px' }}>
                  <textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Add a comment for the review (optional)..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  onClick={closeConfirmDialog}
                  className="submit-btn"
                  style={{ backgroundColor: '#999', flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={loading}
                  className="submit-btn"
                  style={{
                    backgroundColor: confirmAction === 'approve' ? '#28a745' : '#ffa500',
                    flex: 1
                  }}
                >
                  {loading ? 'Processing...' : (confirmAction === 'approve' ? 'Approve' : 'Send Back')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lab Report Modal */}
      {showReportModal && (
        <LabReportTable
          closeModal={() => setShowReportModal(false)}
          patientInfo={referralInfo}
          report={referralInfo}
          vitalInfo={referralInfo?.vital}
          walkin={isWalkin}
          open={showReportModal}
          color={color}
          template={template}
          currentTemplate={1}
          review={true}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {/* Procedure Edit Modal */}
      {showProcedureEditModal && (
          !sendBack ? <ProcedureEditsModal
          open = { showProcedureEditModal }
          closeModal = { () => setShowProcedureEditModal(false) }
          patientInfo = { referralInfo }
          referredPatients = { referredPatients }
          referralId = { referralId }
          readOnly = { readOnly }
          getProcedures = { getProcedures }
          noDoctor = { true }
          walkin = { true }
          review = { manage }
        /> 
        :
      <ProcedureEditsReportModal
        open={showProcedureEditModal}
        closeModal={() => setShowProcedureEditModal(false)}
        patientInfo={referralInfo}
        referredPatients={referredPatients}
        referralId={referralId}
        readOnly={readOnly}
        getProcedures={getProcedures}
        noDoctor={true}
        walkin={true}
        review={manage}

      // manage={manage}
      />
      )}
    </>
  );
}

export default WalkInTable;
